'use client';

import { localeHeaders } from '@repo/i18n/browser';

import { QueryClient, QueryClientProvider } from '@tanstack/react-query';
import { httpBatchLink, httpLink, splitLink } from '@trpc/client';
import { createTRPCReact } from '@trpc/react-query';
import type React from 'react';
import { useMemo, useState } from 'react';
import SuperJSON from 'superjson';
import type { AppRouter } from '@/providers/trpc-client';
import { useOrganizationStore } from '@/stores/organization-store';

export { getQueryKey } from '@trpc/react-query';

export const trpc = createTRPCReact<AppRouter>({
  overrides: {
    useMutation: {
      async onSuccess(opts) {
        await opts.originalFn();

        if (opts.meta.doNotInvalidateQueryOnMutation) {
          return;
        }

        // Invalidate all queries besides ones that specify not to in the meta data.
        await opts.queryClient.invalidateQueries({
          predicate: (query) => !query?.meta?.doNotInvalidateQueryOnMutation,
        });
      },
    },
  },
});

export interface TrpcProviderProps {
  children: React.ReactNode;
  headers?: Record<string, string>;
  apiUrl: string;
  trpcEndpoint: string;
}

export function TrpcProvider({ children, headers, apiUrl, trpcEndpoint }: TrpcProviderProps) {
  const [queryClient] = useState(() => new QueryClient());

  // May cause remounting issues.
  const trpcClient = useMemo(
    () =>
      trpc.createClient({
        links: [
          splitLink({
            condition: (op) => op.context.skipBatch === true,
            true: httpLink({
              url: `${apiUrl}/${trpcEndpoint}`,
              headers: () => {
                const store = useOrganizationStore.getState();
                return {
                  ...headers,
                  ...(store.currentOrganization?.id &&
                    store.currentOrganization?.id !== '' && {
                      'x-organization-id': store.currentOrganization.id,
                    }),
                };
              },
              transformer: SuperJSON,
              fetch: (url, options) => {
                return fetch(url, {
                  ...options,
                  headers: {
                    ...Object.fromEntries(new Headers(options?.headers)),
                    ...localeHeaders(),
                  },
                  credentials: 'include',
                });
              },
            }),
            false: httpBatchLink({
              url: `${apiUrl}/${trpcEndpoint}`,
              headers: (opts) => {
                const store = useOrganizationStore.getState();
                const organizationId = opts.opList.find(
                  (op) =>
                    op.context.organizationId && typeof op.context.organizationId === 'string',
                )?.context.organizationId as string | undefined;

                return {
                  ...headers,
                  ...((organizationId || store.currentOrganization?.id) && {
                    'x-organization-id': organizationId || store.currentOrganization?.id,
                  }),
                };
              },
              transformer: SuperJSON,
              fetch: (url, options) => {
                return fetch(url, {
                  ...options,
                  headers: {
                    ...Object.fromEntries(new Headers(options?.headers)),
                    ...localeHeaders(),
                  },
                  credentials: 'include',
                });
              },
            }),
          }),
        ],
      }),
    [headers, apiUrl, trpcEndpoint],
  );

  return (
    <trpc.Provider client={trpcClient} queryClient={queryClient}>
      <QueryClientProvider client={queryClient}>{children}</QueryClientProvider>
    </trpc.Provider>
  );
}
